/**
 * useShiftContext — Shift-aware task filtering hook (Phase 5)
 *
 * DRR-256: Provides shift context for RA/CNA workflow.
 * DRR-055: Dumb frontend — shift logic computed by backend.
 *
 * Backend resolver: shiftWorkApiMyShiftContext
 * - Determines current shift based on time of day
 * - Returns shift boundaries for task filtering
 */

import { useState, useCallback, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// GraphQL query for shift context
const MY_SHIFT_CONTEXT = gql`
  query MyShiftContext {
    shiftWorkApiMyShiftContext {
      shiftId
      shiftType
      shiftLabel
      shiftStart
      shiftEnd
      facilityId
      actorId
      tenantId
    }
  }
`;

export type ShiftType = 'MORNING' | 'AFTERNOON' | 'NIGHT';

export interface ShiftWindow {
  shiftId: string;
  type: ShiftType;
  label: string;
  shiftStart: string;
  shiftEnd: string;
  facilityId: string | null;
}

export interface ShiftContext {
  currentShift: ShiftWindow | null;
  shiftFilter: 'current' | 'all';
  setShiftFilter: (filter: 'current' | 'all') => void;
  isWithinShift: (dueAt: string | null | undefined) => boolean;
  loading: boolean;
  available: boolean;
}

interface ShiftContextData {
  shiftWorkApiMyShiftContext: {
    shiftId: string;
    shiftType: ShiftType;
    shiftLabel: string;
    shiftStart: string;
    shiftEnd: string;
    facilityId: string | null;
    actorId: string;
    tenantId: string;
  } | null;
}

/**
 * Hook for shift-aware task filtering.
 *
 * Queries backend for current shift context and provides:
 * - Current shift boundaries (start/end times)
 * - Filter to show only tasks due within current shift
 * - isWithinShift() helper for task filtering
 */
export function useShiftContext(): ShiftContext {
  const [shiftFilter, setShiftFilter] = useState<'current' | 'all'>('all');

  const { data, loading } = useQuery<ShiftContextData>(MY_SHIFT_CONTEXT, {
    fetchPolicy: 'cache-and-network',
  });

  const shiftData = data?.shiftWorkApiMyShiftContext;

  const currentShift: ShiftWindow | null = shiftData
    ? {
        shiftId: shiftData.shiftId,
        type: shiftData.shiftType,
        label: shiftData.shiftLabel,
        shiftStart: shiftData.shiftStart,
        shiftEnd: shiftData.shiftEnd,
        facilityId: shiftData.facilityId,
      }
    : null;

  const isWithinShift = useCallback(
    (dueAt: string | null | undefined): boolean => {
      // Without shift data or due date, include all tasks
      if (!currentShift || !dueAt) {
        return true;
      }
      const dueDate = new Date(dueAt).getTime();
      const start = new Date(currentShift.shiftStart).getTime();
      const end = new Date(currentShift.shiftEnd).getTime();
      return dueDate >= start && dueDate <= end;
    },
    [currentShift],
  );

  // Memoize return object to prevent unnecessary re-renders in consumers
  // that include `shift` in useMemo dependency arrays
  return useMemo(
    () => ({
      currentShift,
      shiftFilter,
      setShiftFilter,
      isWithinShift,
      loading,
      available: !!shiftData,
    }),
    [currentShift, shiftFilter, setShiftFilter, isWithinShift, loading, shiftData],
  );
}
